import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useEmbedStore } from '../../lib/embedStore';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { updateUsername, USERNAME_MAX_LENGTH } from '../../lib/api/updateUsername';
import { EmbedBackButton } from '../shared/EmbedBackButton';
import '../MyOffice/MyOfficeEmbed.css';

/** Page de modification du pseudo réservée au mode embed (Arsenal/BPM et TSLH). */
export function MyProfile() {
  const navigate = useNavigate();
  const isEmbed = useEmbedStore((state) => state.isEmbed);
  const { language } = useLanguageStore();
  const currentUsername = useUserStore((state) => state.username);
  const userId = useUserStore((state) => state.user_id);
  const setUsername = useUserStore((state) => state.setUsername);

  const [newUsername, setNewUsername] = useState('');
  const [loading, setLoading] = useState(false);

  // Hors embed, la page historique reste la référence.
  if (!isEmbed) return <Navigate to="/update-username" replace />;

  const fr = language === 'fr';

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!userId) return;

    setLoading(true);
    const result = await updateUsername(userId, newUsername);
    setLoading(false);

    if (!result.success || !result.username) {
      toast.error(result.error || (fr ? 'Erreur lors de la mise à jour' : 'Update failed'));
      return;
    }

    setUsername(result.username);
    toast.success(fr ? 'Pseudo mis à jour avec succès' : 'Username updated successfully');
    navigate('/my-office');
  };

  const trimmed = newUsername.trim();
  const canSubmit = !loading && trimmed.length > 0 && trimmed !== currentUsername;

  return (
    <div className="my-office-embed">
      <EmbedBackButton />

      <header className="my-office-embed__header">
        <div className="my-office-embed__header-main">
          <div className="my-office-embed__eyebrow">{fr ? 'Mon profil' : 'My profile'}</div>
          <h1 className="my-office-embed__title">{fr ? 'Modifier mon pseudo' : 'Change username'}</h1>
        </div>
      </header>

      <section className="my-office-embed__section">
        <form onSubmit={handleSubmit}>
          <div className="moe-field">
            <label className="moe-field__label" htmlFor="current-username">
              {fr ? 'Pseudo actuel' : 'Current username'}
            </label>
            <input
              id="current-username"
              type="text"
              className="moe-input"
              value={currentUsername || ''}
              disabled
            />
          </div>

          <div className="moe-field">
            <label className="moe-field__label" htmlFor="new-username">
              {fr ? 'Nouveau pseudo' : 'New username'}
            </label>
            <input
              id="new-username"
              type="text"
              className="moe-input"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder={fr ? 'Entrez votre nouveau pseudo' : 'Enter your new username'}
              required
              pattern="[a-zA-Z0-9\s]+"
              maxLength={USERNAME_MAX_LENGTH}
              title={fr ? 'Lettres, chiffres et espaces uniquement' : 'Letters, digits and spaces only'}
            />
            <small className="moe-field__hint">
              {fr
                ? `Lettres, chiffres et espaces uniquement — ${USERNAME_MAX_LENGTH} caractères maximum.`
                : `Letters, digits and spaces only — ${USERNAME_MAX_LENGTH} characters max.`}
            </small>
          </div>

          <div className="moe-actions">
            <button type="submit" className="moe-btn" disabled={!canSubmit}>
              {loading ? (fr ? 'Mise à jour…' : 'Updating…') : fr ? 'Mettre à jour' : 'Update'}
            </button>
            <button
              type="button"
              className="moe-btn moe-btn--outline"
              onClick={() => navigate('/my-office')}
              disabled={loading}
            >
              {fr ? 'Annuler' : 'Cancel'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
